import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPencil,
    faGear,
    faUsers,
    faRightFromBracket,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import ArtistMembersPanel from './ArtistMembersPanel';

// One artist row in the picker: avatar, name (inline-renamable), subtitle,
// and pencil / gear / members controls. The members panel and the delete
// confirmation render directly below the row. Mirrors iOS ArtistRowView.
const ArtistRow = ({
    artist,
    isCurrent,
    currentUid,
    projectCount,
    memberCount,
    panelOpen,
    busy,
    rowError,
    memberUids,
    profiles,
    pendingCount,
    onSelect,
    onRename,
    onTogglePanel,
    onLeave,
    onDelete,
    onRemoveMember,
    onInvite,
    onManageLinks,
}) => {
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(artist.name);
    const [savingRename, setSavingRename] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleteDraft, setDeleteDraft] = useState('');
    const inputRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!renaming) return;
        const id = requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        });
        return () => cancelAnimationFrame(id);
    }, [renaming]);

    useEffect(() => {
        if (!menuOpen) return;
        const onDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [menuOpen]);

    const startRename = () => {
        setDraft(artist.name);
        setRenaming(true);
    };

    const commitRename = async () => {
        const trimmed = (draft || '').trim();
        if (!trimmed || trimmed === artist.name) {
            setRenaming(false);
            return;
        }
        setSavingRename(true);
        try {
            await onRename(trimmed);
        } catch {
            // parent surfaces the error; revert the field
        } finally {
            setSavingRename(false);
            setRenaming(false);
        }
    };

    const cancelRename = () => {
        setDraft(artist.name);
        setRenaming(false);
    };

    const handleDelete = async () => {
        try {
            await onDelete();
        } catch {
            setConfirmingDelete(false);
        }
    };

    const subtitle = [
        projectCount === undefined ? null : `${projectCount} project${projectCount === 1 ? '' : 's'}`,
        `${memberCount} member${memberCount === 1 ? '' : 's'}`,
    ].filter(Boolean).join(' · ');

    return (
        <div className="artist-row-wrap">
            <div className={`artist-row ${isCurrent ? 'current' : ''}`}>
                <div className="artist-row-avatar">{artist.getInitials()}</div>

                {renaming ? (
                    <input
                        ref={inputRef}
                        className="editable-input artist-row-rename"
                        value={draft}
                        disabled={savingRename}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                            else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                        }}
                    />
                ) : (
                    <button type="button" className="artist-row-main" onClick={onSelect}>
                        <span className="artist-row-name">{artist.name}</span>
                        <span className="artist-row-subtitle">{subtitle}</span>
                    </button>
                )}

                <div className="artist-row-actions">
                    <button
                        type="button"
                        className="artist-row-iconbtn"
                        disabled={busy || renaming}
                        title="Rename artist"
                        aria-label="Rename artist"
                        onClick={startRename}
                    >
                        <FontAwesomeIcon icon={faPencil} />
                    </button>
                    <div className="artist-gear-wrap" ref={menuRef}>
                        <button
                            type="button"
                            className={`artist-row-iconbtn ${menuOpen ? 'is-active' : ''}`}
                            disabled={busy}
                            title="Artist options"
                            aria-label="Artist options"
                            onClick={() => setMenuOpen((v) => !v)}
                        >
                            <FontAwesomeIcon icon={faGear} />
                        </button>
                        {menuOpen && (
                            <div className="artist-gear-menu">
                                {memberCount > 1 && (
                                    <button
                                        type="button"
                                        className="artist-gear-menu-item"
                                        onClick={() => { setMenuOpen(false); onLeave(); }}
                                    >
                                        <FontAwesomeIcon icon={faRightFromBracket} />
                                        <span>Leave artist</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="artist-gear-menu-item is-destructive"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        setDeleteDraft('');
                                        setConfirmingDelete(true);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                    <span>Delete artist</span>
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className={`artist-members-btn ${panelOpen ? 'is-open' : ''}`}
                        disabled={busy}
                        title="Members"
                        aria-label="Members"
                        onClick={onTogglePanel}
                    >
                        <FontAwesomeIcon icon={faUsers} />
                        <span className="artist-members-btn-count">{memberCount}</span>
                    </button>
                </div>
            </div>

            {rowError && <div className="artist-row-error">{rowError}</div>}

            {confirmingDelete && (
                <div className="artist-confirm-box">
                    <div className="artist-confirm-text">
                        This permanently deletes <strong>{artist.name}</strong> and all its
                        projects for everyone. Type the artist name to confirm.
                    </div>
                    <input
                        className="new-project-input"
                        value={deleteDraft}
                        disabled={busy}
                        placeholder={artist.name}
                        onChange={(e) => setDeleteDraft(e.target.value)}
                    />
                    <div className="new-project-actions">
                        <button
                            type="button"
                            className="btn-ghost"
                            disabled={busy}
                            onClick={() => setConfirmingDelete(false)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary btn-danger"
                            disabled={busy || deleteDraft.trim() !== artist.name}
                            onClick={handleDelete}
                        >
                            {busy ? 'Deleting…' : 'Delete artist'}
                        </button>
                    </div>
                </div>
            )}

            {panelOpen && (
                <ArtistMembersPanel
                    memberUids={memberUids}
                    profiles={profiles}
                    currentUid={currentUid}
                    busy={busy}
                    pendingCount={pendingCount}
                    onRemoveMember={onRemoveMember}
                    onInvite={onInvite}
                    onManageLinks={onManageLinks}
                />
            )}
        </div>
    );
};

export default ArtistRow;
